import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@fox1ck-org/auth/react";
import {
  Alert,
  AlertDescription,
  Button,
  NativeSelect,
  PageHeader,
  PageState,
  SavedViewsBar,
  SearchField,
  SegmentedControl,
  useMediaQuery,
  type SavedView,
} from "@fox1ck-org/ui";
import { DataTable } from "@fox1ck-org/ui/data-table";
import { toast } from "@fox1ck-org/ui/toast";
import { Download, TriangleAlert } from "lucide-react";
import type { SortingState } from "@tanstack/react-table";

import { CampaignColumns } from "@/components/campaigns/campaign-columns";
import { CampaignFilter, describeConditions, useFacets, type FilterDraft } from "@/components/campaigns/campaign-filter";
import { CampaignKpis } from "@/components/campaigns/campaign-kpis";
import { FirstCampaign, NoTrafficInPeriod } from "@/components/campaigns/campaign-empty";
import { MetricValue, OutcomeBar, totalsRow, useCampaignColumns } from "@/components/campaigns/campaign-table";
import { SaveViewDialog } from "@/components/campaigns/save-view-dialog";
import { PlanLimit } from "@/components/plan/plan-limit";
import { CampaignStatusPill, campaignStatusKey } from "@/components/campaigns/status-pill";
import {
  OUTCOME,
  columnCatalog,
  isPercentMetric,
  parseSet,
  setColumns,
  visibleColumns,
  type ListColumn,
  type ListWidth,
  type SetId,
} from "@/components/campaigns/list-columns";
import { bagOf, buildLines, foldLabelFromItems, linesToCsv, type Line } from "@/components/campaigns/list-rows";
import {
  FOLDS,
  LIST_KEYS,
  fromDefinition,
  readState,
  statePatch,
  toDefinition,
  type Fold,
  type ListState,
  type Quick,
} from "@/components/campaigns/list-url";
import { LIST_PAGE, fetchCampaignListAll, useCampaignCount, useCampaignList, useCampaignOwners } from "@/components/campaigns/use-campaign-list";
import { triggerCsvDownload } from "@/lib/csv";
import { useIsPhone } from "@/lib/hooks/use-phone";
import { formatDayRange } from "@/lib/format";
import { useReportParams } from "@/lib/hooks/use-report-params";
import { parseSort, formatSort, resolvePageIndex } from "@/lib/hooks/use-table-params";
import { plural, useT, type TKey } from "@/lib/i18n";
import { toastFailure } from "@/lib/plan";
import { useListCampaignGroups } from "@/lib/services/campaign-service";
import { useListReportViews } from "@/lib/services/report-view-service";
import { useDeleteViewWithUndo } from "@/lib/hooks/use-delete-view";
import { useReportingSchema } from "@/lib/services/reporting-schema-service";
import { useOffersByIds } from "@/lib/services/offer-service";
import { usePeopleNames } from "@/lib/services/workspace-service";
import { useTrackerRoles } from "@/lib/permissions";
import { PhoneActions } from "@/shell/phone";

/**
 * Кампании — the screen a media buyer opens every morning (boards CampList,
 * CampGroups, CampFilter, CampSaveView, CampColumns, CampLoading, CampEmpty,
 * CampError, Camp1920).
 *
 * READ-ONLY (owner, 14.10): no selection, no bulk bar, no status switch, no
 * row that opens into a breakdown. A row is a link to the campaign's card.
 *
 * NO NUMBER OF ITS OWN: rows, the folds' header lines, «Итого», the quick
 * views' counts and the badges are ListCampaigns' answer; the browser
 * formats them and draws the bars.
 *
 * Everything that decides what is on screen is in the address: the Top
 * bar's period, comparison and «Мои/Все»; here the fold (`g`), the quick
 * view (`qv`), the dimension filters (`own`, `src`, `grp`, `geo`, `ofr`,
 * `st`), the conditions (`f`), the search (`q`), the column set (`set`,
 * `cols`), the sort, the saved view (`view`) and the page (`page`).
 *
 * A page is `LIST_PAGE` campaigns of the server's sorted list; «Итого», the
 * folds' headers and the quick views' counts stay the whole list's, and CSV
 * is the whole list.
 *
 * One screen at every width (decision 10): at ≤1440 (board Camp1280) six
 * numbers and «Деньги» without revenue and CPA, the buttons their icons; at
 * ≥1800 (Camp1920) eight and «Деньги + качество»; on a phone (MCampaigns) the
 * same lines as cards, the same quick views, the filter in the header and
 * «Итого»'s profit and ROI over them — the same server totals.
 */

const FOLD_LABEL: Record<Fold, TKey> = {
  list: "camps.fold.list",
  groups: "camps.fold.groups",
  offers: "camps.fold.offers",
  buyers: "camps.fold.buyers",
};
const QUICK_LABEL: Record<Exclude<Quick, "all">, TKey> = {
  losing: "camps.quick.losing",
  noclicks: "camps.quick.noclicks",
  hold50: "camps.quick.hold50",
  archive: "camps.quick.archive",
};

const DEFAULT_SORT = { key: "profit", desc: true };

export default function CampaignsListPage() {
  const t = useT();
  const { isSelf } = useTrackerRoles();
  const navigate = useNavigate();
  const { user } = useAuth();
  const report = useReportParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const wide = useMediaQuery("(min-width: 1800px)");
  const narrow = useMediaQuery("(max-width: 1440px)");
  const phone = useIsPhone();
  const width: ListWidth = wide ? "wide" : narrow ? "narrow" : "base";

  // --- the address -----------------------------------------------------------
  const state = useMemo(() => readState((k) => searchParams.get(k)), [searchParams]);
  const rawSort = parseSort(searchParams.get("sort"))[0];
  const sort = rawSort ? { key: rawSort.id, desc: rawSort.desc } : DEFAULT_SORT;
  const set = parseSet(searchParams.get("set"));
  const mineKeys = useMemo(() => (searchParams.get("cols") ?? "").split(",").filter(Boolean), [searchParams]);
  const viewId = searchParams.get("view") ?? "";
  const pageIndex = resolvePageIndex(searchParams.get("page"));

  /** One write for everything a gesture changes; null deletes a key. */
  const write = useCallback(
    (patch: Record<string, string | null>) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v) next.set(k, v);
            else next.delete(k);
          }
          next.delete("page");
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const patchState = (s: Partial<ListState>) => write(statePatch(s));
  /** The page keeps everything else: it changes which rows are drawn, not which exist. */
  const setPage = useCallback(
    (page: number) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (page > 1) next.set("page", String(page));
          else next.delete("page");
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  // --- the schema's columns ---------------------------------------------------
  const schema = useReportingSchema();
  const catalog = useMemo(() => columnCatalog(schema.data?.metrics ?? [], t("camps.col.outcome")), [schema.data, t]);
  const byKey = useMemo(() => new Map(catalog.map((c) => [c.key, c])), [catalog]);
  const column = useCallback((k: string) => byKey.get(k), [byKey]);
  const isPercent = useCallback((k: string) => isPercentMetric(byKey.get(k)?.def), [byKey]);

  const savedSets = useListReportViews("columns");
  const savedSetKeys = useMemo(
    () => new Map((savedSets.data ?? []).map((v) => [v.id, v.definition?.metricKeys ?? []] as const)),
    [savedSets.data],
  );
  const shownKeys = setColumns(set, { mine: mineKeys, saved: savedSetKeys, width });
  const shown = useMemo(() => visibleColumns(shownKeys, catalog), [shownKeys, catalog]);
  const withDelta = set === "compare" && !!report.compareRange;

  // --- the list -----------------------------------------------------------------
  const query = {
    state,
    range: { from: report.from, to: report.to },
    compare: report.compareRange,
    tz: report.tz,
    mine: report.scope === "mine" ? user?.sub : undefined,
    sort,
    isPercent,
  };
  // The schema only says which conditions are percents: with none, the list
  // goes out beside the schema, not after it.
  const list = useCampaignList(query, pageIndex, !!schema.data || state.conditions.length === 0);
  const data = list.data;

  // Header names: read off the listed campaigns; a fold no listed campaign
  // names (an offer the flow no longer routes to) asks its catalog.
  const groups = useListCampaignGroups();
  const owners = useCampaignOwners();
  const fromItems = useMemo(() => foldLabelFromItems(data?.items ?? [], state.fold), [data?.items, state.fold]);
  const unnamed = useMemo(
    () => (data?.groupTotals ?? []).map((g) => g.key).filter((k) => k && k !== "∅" && !fromItems.has(k)),
    [data?.groupTotals, fromItems],
  );
  const offerNames = useOffersByIds(state.fold === "offers" ? unnamed : [], state.fold === "offers" && unnamed.length > 0);
  const peopleNames = usePeopleNames(state.fold === "buyers" ? unnamed : []);
  const label = useCallback(
    (key: string): string => {
      if (key === "∅") return t("camps.group.spendWithoutClicks");
      const named = fromItems.get(key);
      if (named) return named;
      switch (state.fold) {
        case "groups":
          return key ? (groups.data?.find((g) => g.id === key)?.name ?? key.slice(0, 8)) : t("camps.group.noGroup");
        case "offers":
          return key ? (offerNames.data?.get(key)?.name ?? key.slice(0, 8)) : t("camps.group.noOffer");
        case "buyers":
          return key
            ? (peopleNames.data?.[key] ?? owners.data?.find((o) => o.id === key)?.name ?? t("camps.group.buyer", { id: key.slice(0, 8) }))
            : t("camps.group.noOwner");
        default:
          return key;
      }
    },
    [fromItems, state.fold, groups.data, offerNames.data, peopleNames.data, owners.data, t],
  );

  const lines = useMemo<Line[]>(
    () =>
      data
        ? buildLines({
            items: data.items,
            fold: state.fold,
            groupTotals: data.groupTotals,
            label,
            sort,
            withCompare: !!report.compareRange,
            laterPage: pageIndex > 0,
          })
        : [],
    // sort is rebuilt each render from the URL; its two fields are the dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, state.fold, label, sort.key, sort.desc, report.compareRange, pageIndex],
  );
  const profitMax = useMemo(() => lines.reduce((m, r) => Math.max(m, Math.abs(r.v.profit ?? 0)), 0), [lines]);
  const totals = useMemo(() => (data?.totals ? bagOf(data.totals) : undefined), [data?.totals]);
  const prevTotals = useMemo(() => (report.compareRange && data?.compareTotals ? bagOf(data.compareTotals) : undefined), [data?.compareTotals, report.compareRange]);

  const tableColumns = useCampaignColumns({ columns: shown, withDelta, profitMax, wide });
  const narrowed = state.conditions.length > 0 || state.quick !== "all" || Object.keys(state.facets).length > 0 || state.search !== "";
  const totalsLine = totalsRow({
    label: narrowed
      ? t("camps.total.filtered", { count: data?.total ?? 0 })
      : t("camps.total", { count: data?.total ?? 0, campaigns: plural(t("word.campaigns"), data?.total ?? 0) }),
    columns: shown,
    v: totals,
    prev: prevTotals,
    withDelta,
    profitMax,
  });

  // --- views ------------------------------------------------------------------
  const views = useListReportViews("campaigns");
  const deleteView = useDeleteViewWithUndo();
  const counts = data?.quickCounts;
  // «Активные» is a view like the others — its count beside it — so the bar draws no «All» chip of its own.
  const allView: SavedView = {
    id: "q:all",
    name: report.scope === "mine" ? t("camps.quick.myActive") : t("camps.quick.active"),
    count: state.quick !== "archive" ? counts?.all : undefined,
  };
  const quickViews: SavedView[] = (["losing", "noclicks", "hold50", "archive"] as const).map((q) => ({
    id: `q:${q}`,
    name: t(QUICK_LABEL[q]),
    count: q === "losing" ? counts?.losing : q === "noclicks" ? counts?.noClicks : q === "hold50" ? counts?.holdOverHalf : undefined,
  }));
  const savedViews: SavedView[] = (views.data ?? []).map((v) => ({ id: `v:${v.id}`, name: v.name, isShared: v.scope === "workspace" }));
  const activeChip = viewId ? `v:${viewId}` : `q:${state.quick}`;
  const activeView = views.data?.find((v) => v.id === viewId);

  const clearList: Record<string, null> = Object.fromEntries([...LIST_KEYS, "view"].map((k) => [k, null]));
  const pickChip = (v: SavedView | null) => {
    if (!v || v.id === "q:all") return write(clearList);
    if (v.id.startsWith("q:")) return write({ ...clearList, ...statePatch({ quick: v.id.slice(2) as Quick }), g: state.fold === "list" ? null : state.fold });
    const view = views.data?.find((x) => `v:${x.id}` === v.id);
    if (!view?.definition) return;
    const { state: s, columns, sort: vs } = fromDefinition(view.definition, isPercent);
    write({
      ...clearList,
      ...statePatch(s),
      view: view.id,
      set: columns.length > 0 ? "mine" : null,
      cols: columns.length > 0 ? columns.join(",") : null,
      sort: vs ? formatSort([{ id: vs.key, desc: vs.desc }]) : null,
    });
  };

  const [saving, setSaving] = useState(false);
  const definition = toDefinition(state, shown.map((c) => c.key), sort, isPercent);
  const conditionMetrics = useMemo(() => catalog.filter((c) => c.key !== OUTCOME && c.def), [catalog]);
  const facets = useFacets(conditionMetrics);
  const facetSummary = facets
    .flatMap((f) => {
      const picked = state.facets[f.id as keyof typeof state.facets];
      if (!picked?.length) return [];
      const names = picked.map((v) => f.options?.find((o) => o.value === v)?.label ?? v);
      return [names.join(", ")];
    })
    .concat(state.conditions.length > 0 ? [describeConditions(facets, state.conditions)] : [])
    .join(" · ");
  // What «Сохранить вид» says it keeps: the quick view first («Сливают»), as the bar draws it, then the filters.
  const viewFilters = [state.quick !== "all" ? t(QUICK_LABEL[state.quick]) : "", facetSummary].filter(Boolean).join(" · ");
  const setName = (s: SetId) =>
    s === "mine" ? t("camps.set.mine") : s.startsWith("v:") ? (savedSets.data?.find((v) => `v:${v.id}` === s)?.name ?? t("camps.set.mine")) : t(`camps.set.${s}` as TKey);
  const sortName = `${sort.key === "name" ? t("camps.col.name") : (column(sort.key)?.label ?? sort.key)}, ${sort.desc ? t("camps.sort.desc") : t("camps.sort.asc")}`;

  // --- the filter's draft: the server counts what it would leave -------------------
  const [draft, setDraft] = useState<FilterDraft | undefined>(undefined);
  const draftCount = useCampaignCount(
    { ...query, state: { ...state, facets: draft?.facets ?? state.facets, conditions: draft?.conditions ?? state.conditions } },
    draft !== undefined && !!schema.data,
  );

  const setLabel = set === "money" && wide ? t("camps.set.moneyWide") : setName(set);

  // --- CSV = the screen, every page of it --------------------------------------------
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    if (!data || data.total === 0) {
      toast.error(t("camps.csv.empty"));
      return;
    }
    setExporting(true);
    let items;
    try {
      items = data.total <= data.items.length ? data.items : await fetchCampaignListAll(query);
    } catch (err) {
      toastFailure(err, t("camps.csv.failed"));
      return;
    } finally {
      setExporting(false);
    }
    const named = foldLabelFromItems(items, state.fold);
    const all = buildLines({
      items,
      fold: state.fold,
      groupTotals: data.groupTotals,
      label: (key) => named.get(key) ?? label(key),
      sort,
      withCompare: !!report.compareRange,
    });
    const csv = linesToCsv(
      all,
      shown,
      {
        level: t("camps.csv.level"),
        name: t("camps.col.name"),
        status: t("camps.col.status"),
        group: t("camps.csv.group"),
        campaign: t("camps.csv.campaign"),
        total: t("camps.csv.total"),
      },
      (l) => {
        const k = campaignStatusKey(l.item.campaign?.status);
        return k ? t(k) : "";
      },
      totals,
    );
    triggerCsvDownload(csv, `campaigns-${report.from}_${report.to}.csv`);
  };

  // --- states ---------------------------------------------------------------------
  const [listWithoutNumbers, setListWithoutNumbers] = useState(false);
  const loading = schema.isLoading || (list.isLoading && !data);
  const failedFirst = (list.isError || schema.isError) && !data;
  const noCampaignsAtAll = !!data && data.quickCounts?.all === 0 && !narrowed && report.scope !== "mine";
  const quietPeriod = !!data && (data.quickCounts?.all ?? 0) > 0 && !narrowed && !(totals?.visits ?? 0) && !(totals?.cost ?? 0) && !listWithoutNumbers;

  const sorting: SortingState = [{ id: sort.key, desc: sort.desc }];
  // The footer only when there is a second page: one page needs no pager.
  const pager =
    data && data.total > LIST_PAGE
      ? {
          page: pageIndex + 1,
          pageSize: LIST_PAGE,
          totalCount: data.total,
          onPageChange: setPage,
          labels: {
            previous: t("table.prev"),
            next: t("table.next"),
            perPage: t("table.perPage"),
            summary: (from: number, to: number, total: number) => t("table.summary", { from, to, total }),
            page: (page: number, totalPages: number) => t("table.page", { page, total: totalPages }),
          },
        }
      : undefined;
  const header = (
    <PageHeader
      className="shrink-0"
      title={t("camps.title")}
      titleAddon={
        <SegmentedControl
          size="sm"
          aria-label={t("camps.fold.label")}
          value={state.fold}
          onValueChange={(v: Fold) => patchState({ fold: v })}
          options={FOLDS.filter((f) => !isSelf || f !== "buyers").map((f) => ({ value: f, label: t(FOLD_LABEL[f]) }))}
        />
      }
      actions={
        <>
          <CampaignColumns
            iconOnly={narrow}
            label={t("camps.columns", { set: setLabel })}
            catalog={catalog}
            shown={shown.map((c) => c.key)}
            set={set}
            savedSets={savedSets.data ?? []}
            onSet={(s) => write({ set: s === "money" ? null : s, cols: s === "mine" ? shown.map((c) => c.key).join(",") : null })}
            onMine={(keys) => write({ set: "mine", cols: keys.join(",") })}
            schema={schema.data}
            totals={data?.totals}
          />
          <Button
            variant="secondary"
            size={narrow ? "icon" : undefined}
            aria-label={narrow ? t("camps.csv") : undefined}
            title={narrow ? t("camps.csv") : undefined}
            disabled={exporting}
            onClick={() => void exportCsv()}
          >
            <Download className="size-4" strokeWidth={1.5} />
            {narrow ? null : t("camps.csv")}
          </Button>
        </>
      }
    />
  );

  const filter = (compact: boolean) => (
    <CampaignFilter
      compact={compact}
      facets={facets}
      value={state.facets}
      conditions={state.conditions}
      onApply={(f, cs) => patchState({ facets: f, conditions: cs })}
      onDraft={setDraft}
      found={draftCount.isFetching ? undefined : draftCount.data}
      onSaveAsView={() => setSaving(true)}
    />
  );
  const viewsBar = (
    <SavedViewsBar
      className={phone ? "min-w-0" : "min-w-0 flex-1"}
      variant="chips"
      overflow={phone ? "scroll" : "menu"}
      views={[allView, ...quickViews, ...savedViews]}
      activeId={activeChip}
      allLabel={null}
      onSelect={pickChip}
      onCreate={phone ? undefined : () => setSaving(true)}
      labels={{
        toolbar: t("camps.view.toolbar"),
        create: t("camps.view.add"),
        createAria: t("camps.view.createAria"),
        view: (v) => t(v.isShared ? "camps.view.chipShared" : "camps.view.chip", { name: v.name }),
        delete: (name) => t("camps.view.delete", { name }),
        more: (n) => t("camps.view.more", { n }),
        moreAria: (n) => t("camps.view.moreAria", { n }),
      }}
      onDelete={
        activeView?.canEdit && !phone
          ? () =>
              deleteView(activeView, {
                deleted: t("camps.view.deleted", { name: activeView.name }),
                onDeleted: () => write({ view: null }),
                onRestored: (v) => write({ view: v.id }),
              })
          : undefined
      }
    />
  );
  const staleAlert =
    list.isError && data ? (
      <Alert variant="danger" className="flex shrink-0 items-center gap-3 py-2.5">
        <TriangleAlert className="size-4 shrink-0" />
        <AlertDescription className="text-foreground">
          <b className="font-semibold">{t("camps.error.title")}</b>{" "}
          {t("camps.error.stale", { time: new Date(list.dataUpdatedAt).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }) })}
        </AlertDescription>
        <Button variant="secondary" size="sm" className="ml-auto" onClick={() => void list.refetch()}>
          {t("camps.retry")}
        </Button>
      </Alert>
    ) : null;

  if (phone && !noCampaignsAtAll) {
    const profit = column("profit");
    const roi = column("roi");
    return (
      <div className="flex h-full min-h-0 flex-col gap-2.5 px-3.5 pb-3 pt-3">
        <PhoneActions>{filter(true)}</PhoneActions>
        <div className="-mx-3.5 shrink-0 overflow-x-auto px-3.5 [scrollbar-width:none] [&>*]:w-max [&>*]:flex-nowrap">{viewsBar}</div>
        <div className="flex shrink-0 items-center justify-between gap-3 text-sm">
          <span className="min-w-0 truncate">
            {t("cols.profit")} {totals && profit ? <b className="font-semibold"><MetricValue c={profit} v={totals} /></b> : "—"}
            {" · "}
            {t("cols.roi")} {totals && roi ? <b className="font-semibold"><MetricValue c={roi} v={totals} /></b> : "—"}
          </span>
          <NativeSelect
            aria-label={t("adapt.sort")}
            className="h-11 w-auto shrink-0 border-transparent bg-transparent pr-7 text-sm text-muted-foreground shadow-none"
            value={PHONE_SORTS.includes(sort.key) ? sort.key : "profit"}
            onChange={(e) => {
              const key = e.target.value;
              write({ sort: formatSort([{ id: key, desc: key !== "name" }]) });
            }}
          >
            {PHONE_SORTS.map((k) => (
              <option key={k} value={k}>
                {t(`adapt.sortBy.${k}` as TKey)}
              </option>
            ))}
          </NativeSelect>
        </div>
        {staleAlert}
        {failedFirst ? (
          <PageState mode="error" title={t("camps.error.title")} error={list.error ?? schema.error} retry={() => void list.refetch()} retryLabel={t("camps.retry")} />
        ) : (
          <DataTable
            className="min-h-0 flex-1 rounded-none border-0 bg-transparent"
            fill
            stacked
            stackBelow="md"
            stackedVariant="cards"
            columns={tableColumns}
            data={lines}
            getRowId={(r) => r.id}
            loading={loading}
            skeletonRows={5}
            renderStackedRow={(r) => <PhoneLine line={r} column={column} />}
            pagination={pager}
            caption={t("camps.title")}
            emptyState={
              <PageState
                mode="empty"
                title={t("camps.empty.filtered.title")}
                description={t("camps.empty.filtered.body")}
                action={
                  <Button variant="secondary" size="sm" onClick={() => write(clearList)}>
                    {t("camps.empty.filtered.reset")}
                  </Button>
                }
              />
            }
            labels={{ empty: t("camps.empty.filtered.title"), stackedRow: (r) => (r as Line).name }}
          />
        )}
        <SaveViewDialog
          open={saving}
          onClose={() => setSaving(false)}
          definition={definition}
          summary={{ filters: viewFilters || t("camps.view.noFilters"), fold: t(FOLD_LABEL[state.fold]), columns: setLabel, sort: sortName }}
          onSaved={(v) => write({ view: v.id })}
        />
      </div>
    );
  }

  if (noCampaignsAtAll) {
    return (
      <div className="flex h-full min-h-0 flex-col gap-3 px-5 pb-4 pt-4">
        <PageHeader title={t("camps.title")} />
        <PlanLimit meter="tracker.campaigns" />
        <FirstCampaign />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 px-5 pb-4 pt-4">
      {header}
      <PlanLimit meter="tracker.campaigns" />

      <div className="flex shrink-0 items-center gap-2">
        {viewsBar}
        <span className="ml-auto shrink-0 text-xs text-muted-foreground max-[1440px]:hidden">
          {loading
            ? t("camps.loadingNote", { period: formatDayRange(report.from, report.to) })
            : state.fold !== "list" && data
              ? t("camps.groupsNote", { count: data.groupTotals.length })
              : t("camps.rowNote")}
        </span>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {filter(false)}
        <SearchField
          className="ml-auto w-65"
          value={state.search}
          onChange={(q) => patchState({ search: q })}
          placeholder={t("camps.search")}
          aria-label={t("camps.search")}
        />
      </div>

      {staleAlert}

      {failedFirst ? (
        <PageState mode="error" title={t("camps.error.title")} error={list.error ?? schema.error} retry={() => void list.refetch()} retryLabel={t("camps.retry")} />
      ) : quietPeriod ? (
        <NoTrafficInPeriod
          from={report.from}
          to={report.to}
          campaigns={data?.quickCounts?.all ?? 0}
          onLonger={() => report.setPreset("30d")}
          onShowList={() => setListWithoutNumbers(true)}
        />
      ) : (
        <>
          <CampaignKpis v={totals} prev={prevTotals} loading={loading} column={column} narrow={narrow} />
          <DataTable
            className="min-h-0 flex-1"
            fill
            virtualize
            pinFirstColumn
            bandDividers
            density="compact"
            columns={tableColumns}
            data={lines}
            getRowId={(r) => r.id}
            loading={loading}
            skeletonRows={14}
            sorting={sorting}
            manualSorting
            onSortingChange={(updater) => {
              const next = typeof updater === "function" ? updater(sorting) : updater;
              write({ sort: next.length > 0 ? formatSort(next) : null });
            }}
            onRowClick={(r) => {
              if (r.kind === "campaign") navigate(`/campaigns/${r.campaignId}`);
            }}
            rowClassName={(r) => (r.kind === "group" ? "font-medium [&>td]:bg-surface-2" : undefined)}
            totals={lines.length > 0 ? totalsLine : undefined}
            pagination={pager}
            caption={t("camps.title")}
            emptyState={
              <PageState
                mode="empty"
                title={t("camps.empty.filtered.title")}
                description={t("camps.empty.filtered.body")}
                action={
                  <Button variant="secondary" size="sm" onClick={() => write(clearList)}>
                    {t("camps.empty.filtered.reset")}
                  </Button>
                }
              />
            }
            labels={{ empty: t("camps.empty.filtered.title"), stackedRow: (r) => (r as Line).name }}
          />
        </>
      )}

      <SaveViewDialog
        open={saving}
        onClose={() => setSaving(false)}
        definition={definition}
        summary={{
          filters: viewFilters || t("camps.view.noFilters"),
          fold: t(FOLD_LABEL[state.fold]),
          columns: setLabel,
          sort: sortName,
        }}
        onSaved={(v) => write({ view: v.id })}
      />
    </div>
  );
}

/** What the phone sorts by (доска MCampaigns «по профиту ▾»). */
const PHONE_SORTS = ["profit", "roi", "clicks", "leads", "cost", "name"];

/**
 * A line as a phone card (доска MCampaigns): the name and the status, where
 * it runs, four of its numbers and what became of its leads. The whole card
 * is the link to the campaign; nothing on it switches or selects (decision 10).
 */
function PhoneLine({ line, column }: { line: Line; column: (key: string) => ListColumn | undefined }) {
  const t = useT();
  const cell = (key: string, align?: "end") => {
    const c = column(key);
    return (
      <span className={align === "end" ? "flex min-w-0 flex-col items-end" : "flex min-w-0 flex-col"}>
        <span className="text-xs text-muted-foreground">{c?.short ?? key}</span>
        <span className="truncate text-sm">{c ? <MetricValue c={c} v={line.v} /> : "—"}</span>
      </span>
    );
  };
  const figures = (
    <>
      <span className="grid grid-cols-4 gap-2">
        {cell("clicks")}
        {cell("leads")}
        {cell("profit")}
        {cell("roi", "end")}
      </span>
      <OutcomeBar v={line.v} />
    </>
  );
  if (line.kind === "group") {
    return (
      <div className="flex flex-col gap-2">
        <span className="flex items-baseline gap-2">
          <b className="min-w-0 truncate text-sm font-semibold">{line.name}</b>
          <span className="shrink-0 text-xs text-muted-foreground">{t("camps.group.count", { count: line.campaigns })}</span>
        </span>
        {figures}
      </div>
    );
  }
  const where = [line.item.countries.join(", "), line.item.groupName || line.item.trafficSourceName].filter(Boolean).join(" · ");
  return (
    <Link to={`/campaigns/${line.campaignId}`} className="flex flex-col gap-2">
      <span className="flex min-w-0 items-start gap-2">
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <b className="truncate text-sm font-medium">{line.name}</b>
          {where ? <span className="truncate text-xs text-muted-foreground">{where}</span> : null}
        </span>
        <CampaignStatusPill status={line.item.campaign?.status} />
      </span>
      {figures}
    </Link>
  );
}
